import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { BelowExitPill, Bullet, PlanCard, TrendPill } from './components';
import {
  deriveFocusByDomainInputs,
  fetchFocusByDomain,
  type FocusByDomainContent,
  type FocusByDomainInputs,
} from './domainFocus';
import { cachedFocusByDomain } from './planCache';
import { currentDescriptor, nextDescriptor, type LevelDescriptor } from './planContent';
import { COLORS, NEXT_BOX, NOW_BOX } from './tokens';
import {
  DOMAINS,
  DOMAIN_LABELS,
  belowExit,
  currentLevel,
  domainEvidence,
  domainTrend,
  domainWhatsHappening,
  levelLabel,
  type DomainKey,
  type PlanStudent,
} from './derive';
import { useReclassCriteria } from './reclassCriteriaContext';

type FocusGenState =
  | { status: 'loading' }
  | { status: 'ready'; content: FocusByDomainContent }
  | { status: 'unavailable' }
  | { status: 'missing' }
  | { status: 'error' };

function DetailHeading({ children }: { children: ReactNode }) {
  return (
    <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.muted }}>{children}</Typography>
  );
}

function FocusStatusText({ status }: { status: 'loading' | 'unavailable' | 'missing' | 'error' }) {
  const text =
    status === 'loading'
      ? 'Generating…'
      : status === 'unavailable'
        ? 'No scored data for this domain yet.'
        : status === 'missing'
          ? 'This domain wasn’t generated when this plan was frozen.'
          : 'Couldn’t generate right now.';
  return <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: COLORS.muted }}>{text}</Typography>;
}

function LevelBox({
  kind,
  descriptor,
}: {
  kind: 'now' | 'next';
  descriptor: LevelDescriptor;
}) {
  const box = kind === 'now' ? NOW_BOX : NEXT_BOX;
  const title = kind === 'now' ? 'Now' : 'Next';
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 260,
        bgcolor: box.bg,
        border: `1px solid ${box.border}`,
        borderRadius: 2,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: box.label }}>
        {title} • {descriptor.label} ({descriptor.band})
      </Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1.5, color: COLORS.ink }}>
        {descriptor.text}
      </Typography>
    </Box>
  );
}

function DomainBlock({
  student,
  domain,
  studentId,
  defaultOpen,
  onContent,
  frozen,
  readOnly,
  onSettled,
}: {
  student: PlanStudent;
  domain: DomainKey;
  studentId: string;
  defaultOpen: boolean;
  onContent?: (domain: DomainKey, content: FocusByDomainContent, inputs: FocusByDomainInputs) => void;
  frozen?: FocusByDomainContent | null;
  readOnly?: boolean;
  onSettled?: (domain: DomainKey) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const criteria = useReclassCriteria();
  const contentId = `focus-${studentId}-${domain}`;
  const level = currentLevel(student, domain);
  const trend = domainTrend(student, domain);
  const pill = { category: trend.category, label: trend.label };
  const below = belowExit(student, criteria, domain);
  const now = currentDescriptor(domain, level);
  const next = nextDescriptor(domain, level);

  const inputs = useMemo(
    () => (frozen || readOnly ? null : deriveFocusByDomainInputs(student, domain, criteria)),
    [frozen, readOnly, student, domain, criteria],
  );
  const [fetched, setGen] = useState<FocusGenState>(inputs ? { status: 'loading' } : readOnly ? { status: 'missing' } : { status: 'unavailable' });
  const gen: FocusGenState = frozen ? { status: 'ready', content: frozen } : fetched;
  useEffect(() => {
    if (!inputs) {
      onSettled?.(domain);
      return;
    }
    let cancelled = false;
    cachedFocusByDomain(studentId, domain, inputs)
      .then((saved) => saved ?? fetchFocusByDomain(studentId, inputs))
      .then((content) => {
        if (cancelled) return;
        if (content.recommendation) {
          setGen({ status: 'ready', content });
          onContent?.(domain, content, inputs);
        } else {
          setGen({ status: 'error' });
        }
      })
      .catch(() => {
        if (!cancelled) setGen({ status: 'error' });
      })
      .finally(() => {
        if (!cancelled) onSettled?.(domain);
      });
    return () => {
      cancelled = true;
    };
  }, [inputs, studentId, domain, onContent, onSettled]);

  return (
    <Box sx={{ border: `1px solid ${COLORS.divider}`, borderRadius: 2, overflow: 'hidden' }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          px: 2,
          py: 2,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(8,8,27,0.02)' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>
              {DOMAIN_LABELS[domain]}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: COLORS.muted }}>
              {level != null ? `${level.toFixed(1)} — ${levelLabel(level)}` : 'No scored data'}
            </Typography>
          </Box>
          <TrendPill category={pill.category} label={pill.label} />
          {below && <BelowExitPill />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: COLORS.navy }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, letterSpacing: '0.5px' }}>
            {open ? 'Hide Details' : 'Show Details'}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{ fontSize: 20, transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }}
          />
        </Box>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box
          id={contentId}
          sx={{
            px: 2,
            pb: 2,
            pt: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {now && next && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <LevelBox kind="now" descriptor={now} />
              <LevelBox kind="next" descriptor={next} />
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <DetailHeading>What&rsquo;s Happening</DetailHeading>
            <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1.5, color: COLORS.ink }}>
              {domainWhatsHappening(student, domain)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <DetailHeading>Recommendation</DetailHeading>
            {gen.status === 'ready' ? (
              <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1.5, color: COLORS.ink }}>
                {gen.content.recommendation}
              </Typography>
            ) : (
              <FocusStatusText status={gen.status} />
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <DetailHeading>Domain Supports</DetailHeading>
            {gen.status === 'ready' ? (
              gen.content.accommodations.map((a) => <Bullet key={a}>{a}</Bullet>)
            ) : (
              <FocusStatusText status={gen.status} />
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <DetailHeading>Evidence</DetailHeading>
            <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1.5, color: COLORS.ink }}>
              {domainEvidence(student, domain)}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export function FocusByDomain({
  student,
  studentId,
  onContent,
  frozen = null,
  onSettled,
}: {
  student: PlanStudent;
  studentId: string;
  onContent?: (domain: DomainKey, content: FocusByDomainContent, inputs: FocusByDomainInputs) => void;
  frozen?: Record<string, FocusByDomainContent> | null;
  onSettled?: (domain: DomainKey) => void;
}) {
  const firstName = student.firstName || 'this student';
  return (
    <PlanCard
      title="Focus By Domain"
      subtitle={`Recommendations and supports calibrated to move ${firstName} from the current level to the next.`}
      sx={{ gap: 2 }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {DOMAINS.map((d) => (
          <DomainBlock
            key={`${studentId}-${d}`}
            student={student}
            domain={d}
            studentId={studentId}
            defaultOpen={false}
            onContent={onContent}
            frozen={frozen ? frozen[d] ?? null : null}
            readOnly={frozen !== null}
            onSettled={onSettled}
          />
        ))}
      </Box>
    </PlanCard>
  );
}
